import { Link } from 'react-router-dom';
import { HomeStaticImage } from './HomeStaticImage';
import type { HomeExclusiveDrop } from './homeDrops';
import { homeDropInspirationCta } from './homePopularDrops';

type HomeDropTileProps = Readonly<{
  drop: HomeExclusiveDrop;
  priority?: boolean;
  variant?: 'rail' | 'grid';
  tileIndex?: number;
}>;

/**
 * Style-inspiration tile for the home rail — links to live shop, not fictional PDPs.
 */
export function HomeDropTile({
  drop,
  priority = false,
  variant = 'grid',
  tileIndex = 0,
}: HomeDropTileProps) {
  const isRail = variant === 'rail';
  const ctaLabel = homeDropInspirationCta(tileIndex);

  const sizeClass = isRail
    ? 'h-[300px] w-[210px] sm:h-[320px] sm:w-[230px]'
    : 'h-[300px] w-[220px] sm:h-[320px] sm:w-[240px] md:h-auto md:w-full md:aspect-[3/4] md:shrink';

  return (
    <Link
      to="/shop"
      className={`group relative block shrink-0 snap-start overflow-hidden rounded-[28px] border border-purple-100/70 bg-gradient-to-br from-slate-100 via-purple-50 to-slate-100 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${sizeClass}`}
    >
      <HomeStaticImage
        src={drop.image}
        alt={`${drop.name} — style inspiration`}
        priority={priority}
        profile="rail"
        className="absolute inset-0 h-full w-full object-cover object-[center_36%] transition-transform duration-500 group-hover:scale-[1.03]"
      />

      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50"
        aria-hidden
      />

      <div className="relative flex h-full flex-col justify-between p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="rounded-full bg-white/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white/90 backdrop-blur-sm">
            Style inspiration
          </span>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-white/90 leading-tight text-right">
            {drop.category}
          </p>
        </div>

        <div>
          <p className="line-clamp-2 text-base font-extrabold leading-tight text-white drop-shadow-md sm:text-lg">
            {drop.name}
          </p>
          <p className="mt-2 text-[11px] font-semibold text-white/85">
            {ctaLabel}
          </p>
        </div>
      </div>
    </Link>
  );
}
